import React, { useState, useEffect, useRef } from "react";
import * as Styled from "./styles";
import { ASSETS } from "../../utils/config";
import UserItem from "./UserItem";
import { TextView } from "@components";
import WheelItem from "./WheelItem";
import { searchUsers, searchWheels } from "../../services/api";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "../Tabs";
import { AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

const mainTabs = ["Users", "Wheels"];

export default function SearchBar({
  setModalOpen = null,
  setOnLeave = null,
  handleNavigate = null,
}) {
  const [searchText, setSearchText] = useState("");
  const [wheels, setWheels] = useState([]);
  const [users, setUsers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchContainerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedTab, setSelectedTab] = useState(mainTabs[0]);

  const [pageUsers, setPageUsers] = useState(1);
  const [pageWheels, setPageWheels] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [hasMoreWheels, setHasMoreWheels] = useState(true);
  const itemsContainerRef = useRef(null);

  const wheelInformation = useSelector((state) => state.segmentBuilder);
  const prevIndexRef = useRef(wheelInformation?.currentIndex);

  const fetchData = async (text, type, page) => {
    if (!text.trim()) {
      setUsers([]);
      setWheels([]);
      setShowResults(false);
      return;
    }

    try {
      setLoading(true);
      let response;

      if (type === "Users") {
        response = await searchUsers(text, page, 10);
        const { results } = response.data;

        if (page === 1) {
          setUsers(results);
        } else {
          setUsers((prevUsers) => [...prevUsers, ...results]);
        }

        setHasMoreUsers(results.length === 10);

        if (page === 1) {
          const hasResults = results.length > 0;
          setShowResults(hasResults);
        }
      } else if (type === "Wheels") {
        response = await searchWheels(text, page, 10);
        const { results } = response.data;

        if (page === 1) {
          setWheels(results);
        } else {
          setWheels((prevWheels) => [...prevWheels, ...results]);
        }

        setHasMoreWheels(results.length === 10);

        if (page === 1) {
          const hasResults = results.length > 0;
          setShowResults(hasResults);
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(`Error fetching ${type.toLowerCase()} results:`, error);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    setPageUsers(1);
    setPageWheels(1);
    setHasMoreUsers(true);
    setHasMoreWheels(true);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    const newTimeout = setTimeout(() => {
      fetchData(value, selectedTab, 1);
    }, 500);

    setTypingTimeout(newTimeout);
  };

  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading) return;

      const container = itemsContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          // Adjust threshold as needed
          if (selectedTab === "Users" && hasMoreUsers) {
            const nextPage = pageUsers + 1;
            fetchData(searchText, "Users", nextPage);
            setPageUsers(nextPage);
          } else if (selectedTab === "Wheels" && hasMoreWheels) {
            const nextPage = pageWheels + 1;
            fetchData(searchText, "Wheels", nextPage);
            setPageWheels(nextPage);
          }
        }
      }
    };

    const container = itemsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    loading,
    hasMoreUsers,
    hasMoreWheels,
    selectedTab,
    searchText,
    pageUsers,
    pageWheels,
  ]);

  useEffect(() => {
    if (searchText.trim()) {
      setLoading(true);
      if (selectedTab === "Users") {
        setPageUsers(1);
        setHasMoreUsers(true);
        setUsers([]);
        fetchData(searchText, "Users", 1);
      } else if (selectedTab === "Wheels") {
        setPageWheels(1);
        setHasMoreWheels(true);
        setWheels([]);
        fetchData(searchText, "Wheels", 1);
      }
    } else {
      // If search text is cleared, reset results
      setUsers([]);
      setWheels([]);
      setShowResults(false);
    }
  }, [selectedTab]);

  const onCancel = () => {
    setSearchText("");
    setShowResults(false);
    setUsers([]);
    setWheels([]);
  };

  const iconVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      scale: [0.8, 1.2, 1],
      rotate: [-10, 10, 0],
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
    exit: { opacity: 0 },
  };

  const clearIconVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.7 },
    hover: { scale: 1.3 },
    tap: { scale: 0.9 },
  };

  return (
    <Styled.Container ref={searchContainerRef}>
      <Styled.SearchContainer>
        <AnimatePresence initial={false}>
          {isFocused ? (
            <Styled.SearchIcon
              key="active"
              src={ASSETS.SearchIconActive}
              alt="Active Search Icon"
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          ) : (
            <Styled.SearchIcon
              key="inactive"
              src={ASSETS.SearchIcon2}
              alt="Inactive Search Icon"
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          )}
        </AnimatePresence>
        <Styled.TextInput
          onChange={handleInputChange}
          placeholder="Search iYuga"
          value={searchText}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {searchText && (
          <Styled.ClearIcon
            key="clear"
            src={ASSETS.ClearIcon}
            onClick={onCancel}
            variants={clearIconVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            whileHover="hover"
            whileTap="tap"
            alt="Clear Search"
            aria-label="Clear search input"
            role="button"
            tabIndex={0}
          />
        )}
      </Styled.SearchContainer>
      {(showResults || searchText || loading) && (
        <Styled.ResultsContainer>
          <>
            <Styled.TabsContainer>
              <Tabs
                categories={mainTabs}
                selectedCategory={selectedTab}
                onCategoryChange={(_, val) => setSelectedTab(val)}
                tabStyle={{ maxWidth: "20%" }}
              />
            </Styled.TabsContainer>

            <Styled.ItemsContainer ref={itemsContainerRef}>
              {selectedTab === "Users" && users.length > 0 && (
                <Styled.ResultType>
                  {users.map((item) => (
                    <UserItem
                      key={item.id}
                      item={item}
                      setSearchText={setSearchText}
                      setModalOpen={setModalOpen}
                      setOnLeave={setOnLeave}
                      wheelInformation={wheelInformation}
                      prevIndexRef={prevIndexRef}
                      setShowResults={setShowResults}
                      handleNavigate={handleNavigate}
                    />
                  ))}
                </Styled.ResultType>
              )}

              {selectedTab === "Wheels" && wheels.length > 0 && (
                <Styled.ResultType>
                  {wheels.map((item) => (
                    <WheelItem
                      key={item.id}
                      item={item}
                      setModalOpen={setModalOpen}
                      setOnLeave={setOnLeave}
                      wheelInformation={wheelInformation}
                      prevIndexRef={prevIndexRef}
                      setShowResults={setShowResults}
                      handleNavigate={handleNavigate}
                    />
                  ))}
                </Styled.ResultType>
              )}

              {!loading &&
                searchText &&
                ((selectedTab === "Users" && users.length === 0) ||
                  (selectedTab === "Wheels" && wheels.length === 0)) && (
                  <Styled.Loader>
                    <TextView
                      style={{
                        fontWeight: 500,
                        marginLeft: 10,
                        marginBottom: 5,
                      }}
                    >
                      {selectedTab === "Users"
                        ? "No Users Found"
                        : "No Wheels Found"}
                    </TextView>
                  </Styled.Loader>
                )}

              {/* Loader for infinite scroll */}
              {loading && (
                <Styled.InfiniteLoader>
                  <CircularProgress size={24} />
                </Styled.InfiniteLoader>
              )}
            </Styled.ItemsContainer>
          </>
        </Styled.ResultsContainer>
      )}
    </Styled.Container>
  );
}
